package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document

/**
 * Premium styling for the admin Dashboard screen. Mirrors the Menu page's
 * emerald hero (Unbounded display font, dotted glow, glassy stat chips) so the
 * first screen an owner sees after creating a venue sells the product: page
 * identity on a deep-green gradient, the scan counter with a trend pill and a
 * mint sparkline, and the four key numbers as frosted chips inside the hero.
 * Injected once as plain CSS — gradients, blur and hover states are easier to
 * express here than through the Compose modifier layer.
 */
@Composable
internal fun InjectDashboardStyles() {
    LaunchedEffect(Unit) {
        val style = document.getElementById("qk-dash-styles") ?: document.createElement("style").also {
            it.setAttribute("id", "qk-dash-styles")
            document.head?.appendChild(it)
        }
        style.textContent = DASH_CSS
    }
}

// language=CSS
private const val DASH_CSS = """
.qk-dash{--e:#0F9D6E;--ed:#0B7A55;--mint:#7EE2BC;width:100%}
.qk-dash-disp{font-family:'Unbounded',system-ui,sans-serif;letter-spacing:-.02em}

/* ---- hero ---- */
.qk-dash-hero{position:relative;overflow:hidden;border-radius:24px;padding:26px 28px;margin-bottom:16px;color:#fff;
  background:linear-gradient(135deg,#12A574 0%,#0B7A55 58%,#0A5C42 100%);box-shadow:0 22px 50px -22px rgba(11,122,85,.7)}
.qk-dash-hero::after{content:'';position:absolute;top:-40px;right:-30px;width:280px;height:220px;pointer-events:none;opacity:.5;
  background-image:radial-gradient(rgba(255,255,255,.5) 2px,transparent 2.4px);background-size:15px 15px;
  -webkit-mask-image:linear-gradient(215deg,#000,transparent 76%);mask-image:linear-gradient(215deg,#000,transparent 76%)}
.qk-dash-hero-head{display:flex;align-items:center;gap:16px;position:relative;z-index:2}
.qk-dash-hero-head .lang{margin-left:auto;align-self:flex-start}
.qk-dash-hero .ic{width:52px;height:52px;min-width:52px;border-radius:16px;background:rgba(255,255,255,.16);
  display:inline-flex;align-items:center;justify-content:center;color:#fff;
  backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.qk-dash-hero .ic svg{width:26px;height:26px}
.qk-dash-hero h1{font-size:26px;font-weight:700;margin:0}
.qk-dash-hero p{font-size:13.5px;color:rgba(255,255,255,.85);margin:5px 0 0;max-width:520px;line-height:1.45}

/* scan counter + trend + sparkline */
.qk-dash-main{display:flex;flex-wrap:wrap;align-items:flex-end;justify-content:space-between;gap:16px;
  margin-top:20px;position:relative;z-index:1}
.qk-dash-over{display:block;font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:rgba(255,255,255,.6)}
.qk-dash-numrow{display:flex;align-items:baseline;gap:12px;flex-wrap:wrap}
.qk-dash-num{font-family:'Unbounded',system-ui,sans-serif;font-size:44px;font-weight:700;line-height:1.05;color:#fff;letter-spacing:-.02em}
.qk-dash-delta{display:inline-flex;align-items:center;border-radius:999px;padding:4px 11px;font-size:11.5px;font-weight:700;white-space:nowrap}
.qk-dash-delta.up{background:rgba(126,226,188,.18);color:var(--mint)}
.qk-dash-delta.down{background:rgba(255,156,140,.18);color:#FF9C8C}
.qk-dash-spark{flex:1;min-width:150px;max-width:250px}

/* frosted stat chips */
.qk-dash-chips{display:flex;flex-wrap:wrap;gap:10px;margin-top:20px;position:relative;z-index:1}
.qk-dash-chip{display:flex;align-items:center;gap:10px;background:rgba(255,255,255,.13);border:1px solid rgba(255,255,255,.14);
  border-radius:14px;padding:10px 15px;min-width:110px;backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.qk-dash-chip .i{width:32px;height:32px;min-width:32px;border-radius:10px;background:rgba(255,255,255,.14);
  display:inline-flex;align-items:center;justify-content:center;color:#fff}
.qk-dash-chip .i svg{width:16px;height:16px}
.qk-dash-chip b{display:block;font-family:'Unbounded',system-ui,sans-serif;font-size:17px;font-weight:700;line-height:1.1;color:#fff;white-space:nowrap}
.qk-dash-chip span{display:block;font-size:10.5px;color:rgba(255,255,255,.82);letter-spacing:.02em;margin-top:2px;text-transform:lowercase;white-space:nowrap}

/* ---- section title with an emerald icon chip ---- */
.qk-dash-ct{display:flex;align-items:center;gap:10px;font-size:16px;font-weight:700;color:#14201B}
.qk-dash-ct i{width:30px;height:30px;min-width:30px;border-radius:10px;background:rgba(15,157,110,.1);color:var(--ed);
  display:inline-flex;align-items:center;justify-content:center}
.qk-dash-ct i svg{width:16px;height:16px}

/* ---- compact empty state inside cards ---- */
.qk-dash-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;
  width:100%;box-sizing:border-box;padding:26px 16px;border:1px dashed #DAD4CB;border-radius:16px;margin-top:4px}
.qk-dash-empty .ic{width:46px;height:46px;border-radius:14px;margin-bottom:10px;display:inline-flex;align-items:center;justify-content:center;
  color:var(--ed);background:rgba(15,157,110,.1)}
.qk-dash-empty .ic svg{width:22px;height:22px}
.qk-dash-empty b{font-size:14px;font-weight:700;color:#14201B}
.qk-dash-empty span{font-size:12.5px;color:#6B7280;margin-top:4px;max-width:320px;line-height:1.45}

/* muted footnote under an empty chart */
.qk-dash-note{display:block;text-align:center;font-size:12px;color:#9CA3AF;margin-top:8px}

/* ---- phones: the revenue chip must keep "1234.56 €" on one line, so the
   chips become a 2-column grid and the value scales down instead of wrapping ---- */
@media(max-width:560px){
  .qk-dash-hero{padding:20px 18px}
  .qk-dash-num{font-size:34px}
  .qk-dash-chips{display:grid;grid-template-columns:repeat(2,minmax(0,1fr))}
  .qk-dash-chip{min-width:0;padding:10px 12px}
  .qk-dash-chip>div{min-width:0}
  .qk-dash-chip b{font-size:clamp(12px,4.2vw,17px)}
}
"""
